import React from "react";

import "./IntakeForm.css";

import { Formik, Form, Field, getIn } from "formik";

// ui
import { Alert, Row, Col, Container } from "reactstrap";
import TextInput from "../Form/TextInput";
import PhoneInput from "../Form/PhoneInput";
import DropDown from "../Form/DropDown";
import CheckBox from "../Form/CheckBox";
import CheckBoxGroup from "../Form/CheckBoxGroup";
import SectionHeader from "../Form/SectionHeader";
import Button from "../Form/Button";
import { stateOptions } from "./fields";

import { validationSchema, defaultValues } from "./fields";

import CallCenterSection from "./CallCenterSection";
import TopInfo from "./TopInfo";
import Header from "./HeaderInfo";
import BottomInfo from "./BottomInfo";
import RequiredIndicator from "../Form/RequiredIndicator";
import FormFieldGroup from "../Form/FormFieldGroup";

const IntakeForm = (props) => {
	const { setError, error, handleSubmit, isInternalUser } = props;

	return (
		<Container>
			<Header isInternalUser={isInternalUser} />
			{isInternalUser ? error && <Alert color="danger">{error}</Alert> : ""}

			<Formik
				initialValues={defaultValues}
				enableReinitialize={true}
				validationSchema={validationSchema}
				validateOnChange={true}
				onSubmit={handleSubmit}
			>
				{({
					values,
					errors,
					touched,
					isSubmitting,
					setFieldValue,
					setFieldError,
					validateForm,
				}) => (
					<Form>
						<CallCenterSection
							values={values}
							errors={errors}
							touched={touched}
							isInternalUser={isInternalUser}
							setFieldValue={setFieldValue}
							setFieldError={setFieldError}
						/>
						<TopInfo isInternalUser={isInternalUser} />

						<Row>
							<Col md="10">
								<Row>
									<Col>
										{!isInternalUser ? (
											<div className="mb-2">
												<RequiredIndicator /> Indicates a required field
											</div>
										) : (
											""
										)}
										{!isInternalUser && error && (
											<Alert color="danger">{error}</Alert>
										)}
										<SectionHeader
											header="Requests to T. Rowe Price are permitted for the types of relationships listed below"
											subHeader="(check all that apply to you and for which you want to make a request)"
											required
										/>

										<br />
										<FormFieldGroup error={errors["trpRelChecked"]}>
											<Field name="trpRel.ii">
												{({ field }) => (
													<CheckBox
														{...field}
														value={true}
														currentValue={getIn(values, field.name)}
														touched={getIn(touched, field.name)}
														error={getIn(errors, field.name)}
														label="I never had or applied for a personal T. Rowe Price mutual fund or brokerage account or advisory product/service, but I have received promotional or educational material from T. Rowe Price."
													/>
												)}
											</Field>
											<Field name="trpRel.b2b1">
												{({ field }) => (
													<CheckBox
														{...field}
														value={true}
														currentValue={getIn(values, field.name)}
														touched={getIn(touched, field.name)}
														error={getIn(errors, field.name)}
														label="I’m an employee or agent of a company/entity that has, had, or applied for a T. Rowe Price mutual fund or brokerage account or advisory product/service."
													/>
												)}
											</Field>
											<Field name="trpRel.b2b2">
												{({ field }) => (
													<CheckBox
														{...field}
														value={true}
														currentValue={getIn(values, field.name)}
														touched={getIn(touched, field.name)}
														error={getIn(errors, field.name)}
														label="I’m an employee or agent of a company/entity that interacts with T. Rowe Price relating to financial products or services, such as a Registered Investment Advisor (RIA), 
														intermediary, financial advisor, retirement plan sponsor, retirement plan consultant, trust/foundation/endowment, or other institution."
													/>
												)}
											</Field>
											<Field name="trpRel.b2b3">
												{({ field }) => (
													<CheckBox
														{...field}
														value={true}
														currentValue={getIn(values, field.name)}
														touched={getIn(touched, field.name)}
														error={getIn(errors, field.name)}
														label="I’m an employee or agent of a company/entity that is a vendor or other counterparty to T. Rowe Price."
													/>
												)}
											</Field>
											<Field name="trpRel.dir">
												{({ field }) => (
													<CheckBox
														{...field}
														value={true}
														currentValue={getIn(values, field.name)}
														touched={getIn(touched, field.name)}
														error={getIn(errors, field.name)}
														label="I hold or held stock of T. Rowe Price Group, Inc. directly in my name."
													/>
												)}
											</Field>
											<Field name="trpRel.employee">
												{({ field }) => (
													<CheckBox
														{...field}
														value={true}
														currentValue={getIn(values, field.name)}
														touched={getIn(touched, field.name)}
														error={getIn(errors, field.name)}
														label="I'm a current or former employee of T. Rowe Price."
													/>
												)}
											</Field>
											<Field name="trpRel.job">
												{({ field }) => (
													<CheckBox
														{...field}
														value={true}
														currentValue={getIn(values, field.name)}
														touched={getIn(touched, field.name)}
														error={getIn(errors, field.name)}
														label="I applied for a job with T. Rowe Price, or supplied personal information 
														as a prospective employee or contractor, but did not become an employee or contractor"
													/>
												)}
											</Field>
											<Field name="trpRel.contractor">
												{({ field }) => (
													<CheckBox
														{...field}
														value={true}
														currentValue={getIn(values, field.name)}
														touched={getIn(touched, field.name)}
														error={getIn(errors, field.name)}
														label="I’m a current or former contractor for T. Rowe Price."
													/>
												)}
											</Field>
											<Field name="trpRel.na" key="trpRel.na">
												{({ field }) => (
													<CheckBox
														{...field}
														value={true}
														currentValue={getIn(values, field.name)}
														touched={getIn(touched, field.name)}
														error={getIn(errors, field.name)}
														label="I don’t fit into any of the categories above, but I still think T. Rowe Price has my personal information (e.g., I used a tool on your website). 
														[We will contact you to get more details.]"
													/>
												)}
											</Field>
										</FormFieldGroup>
									</Col>
								</Row>
								<Row>
									<Col>
										<SectionHeader
											header="Type of request"
											subHeader="My request is for (check all that apply)"
											required
										/>
										<CheckBoxGroup
											error={errors["requestChecked"]}
											boxes={[
												{
													fieldName: "requestAccess",
													currentValue: values["requestAccess"],
													value: true,
													touched: touched["requestAccess"],
													label:
														"Right to know & access to personal information you have about me",
												},
												{
													fieldName: "requestCorrection",
													currentValue: values["requestCorrection"],
													value: true,
													touched: touched["requestCorrection"],
													label:
														"Correction to personal information you have about me [We will contact you to get more details.]",
												},
												{
													fieldName: "requestDelete",
													currentValue: values["requestDelete"],
													value: true,
													touched: touched["requestDelete"],
													label:
														"Deletion of personal information you have about me",
												},
											]}
											messageText="* Please note that there are exceptions that can apply to each type of request."
										/>
									</Col>
									{/* <Col>
										<SectionHeader
											header="dumyf request"
											subHeader="My request is for (check all that apply)"
											required
										/>
										<CheckBoxGroup
											error={errors["dummytest"]}
											boxes={[
												{
													fieldName: "dummy.test1",
													currentValue: values["dummy.test1"],
													value: true,
													touched: touched["dummy.test1"],
													label: "dummy test1",
												},
												{
													fieldName: "dummy.test2",
													currentValue: values["dummy.test2"],
													value: true,
													touched: touched["dummy.test2"],
													label: "dummy test2",
													setFieldValue: "checked",
												},
											]}
											messageText="* Please note ."
										/>
									</Col> */}
								</Row>

								<Row>
									<Col md="10">
										<SectionHeader
											header="Name and contact details"
											subHeader="Please use the information you believe we have/may have on file for you. 
											If you have updated contact details you’d like us to use in responding to you, please also give us your current details where indicated."
										/>
									</Col>

									<Col md="4">
										<Field name="firstName">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="First Name"
													type="text"
													maxLength="50"
													required
												/>
											)}
										</Field>
									</Col>
									<Col md="2">
										<Field name="middleName">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Middle"
													type="text"
													maxLength="50"
												/>
											)}
										</Field>
									</Col>
									<Col md="4">
										<Field name="lastName">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Last Name"
													type="text"
													maxLength="50"
													required
												/>
											)}
										</Field>
									</Col>
									<Col md="2">
										<Field name="suffix">
											{({ field }) => (
												<DropDown
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Suffix"
													options={[
														{ label: "", value: "" },
														{ label: "JR", value: "JR" },
														{ label: "SR", value: "SR" },
														{ label: "II", value: "II" },
														{ label: "III", value: "III" },
													]}
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="8">
										<Field name="address1OnFile">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Address on file (First Line)"
													type="text"
													maxLength="35"
													required
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="8">
										<Field name="address2OnFile">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Address on file (Second Line)"
													type="text"
													maxLength="35"
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="8">
										<Field name="cityOnFile">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="City on file"
													type="text"
													maxLength="35"
													required
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="3">
										<Field name="stateOnFile">
											{({ field }) => (
												<DropDown
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="State on file"
													options={stateOptions}
													required
												/>
											)}
										</Field>
									</Col>
									<Col md="3">
										<Field name="zipOnFile">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Zip Code on file"
													type="text"
													required
													maxLength="5"
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="8">
										<Field name="companyOnFile">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Company/entity name on file"
													maxLength="50"
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="6">
										<Field name="phoneOnFile">
											{({ field }) => (
												<PhoneInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Phone number on file"
													type="text"
													required
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="6">
										<Field name="emailOnFile">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Email address on file"
													type="text"
													maxLength="256"
													required
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="6">
										<Field name="address1">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Current Address if different (First Line)"
													type="text"
													nullable="true"
													maxLength="35"
													required
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="6">
										<Field name="address2">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Current Address if different (Second Line)"
													type="text"
													maxLength="35"
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="6">
										<Field name="city">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Current City if different"
													type="text"
													nullable="true"
													maxLength="35"
													required
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="3">
										<Field name="state">
											{({ field }) => (
												<DropDown
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Current State if different"
													options={[
														{ value: "", label: "" },
														{ value: "CA", label: "California" },
													]}
													nullable="true"
													required
												/>
											)}
										</Field>
									</Col>
									<Col md="3">
										<Field name="zip">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Current Zip if different"
													type="text"
													maxLength="5"
													nullable="true"
													required
												/>
											)}
										</Field>
									</Col>
								</Row>
								<Row>
									<Col md="6">
										<Field name="phone">
											{({ field }) => (
												<PhoneInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Current phone number if different"
													type="text"
													nullable="true"
													required
												/>
											)}
										</Field>
									</Col>
								</Row>

								<Row>
									<Col md="6">
										<Field name="email">
											{({ field }) => (
												<TextInput
													{...field}
													touched={touched[field.name]}
													error={errors[field.name]}
													label="Current email address if different"
													type="text"
													maxLength="256"
													nullable="true"
													required
												/>
											)}
										</Field>
									</Col>
								</Row>

								<Row className="currentCompany">
									<Col>
										<Field name="company">
											{({ field }) => (
												<TextInput
													{...field}
													id="company-name"
													label="Current company/entity name if different"
													type="text"
													tabIndex="-1"
												/>
											)}
										</Field>
									</Col>
								</Row>

								<BottomInfo />
								<Row>
									<Col>
										<Button
											type="submit"
											disabled={isSubmitting}
											color="primary"
											onClick={async () => {
												const curErrors = await validateForm();
												const curErrorsKeys = Object.keys(curErrors);
												if (curErrorsKeys.length > 0) {
													setError(
														"Please enter the required fields and try again."
													);
												}
											}}
										>
											{isSubmitting ? "Saving..." : "Submit"}
										</Button>
									</Col>
								</Row>
							</Col>
						</Row>
					</Form>
				)}
			</Formik>
			<Row className="mt-3">
				<Col className="ml-2">
					<a
						href="https://www.troweprice.com/privacy"
						target="_blank"
						rel="noopener noreferrer"
					>
						Privacy Notice
					</a>
				</Col>
			</Row>
		</Container>
	);
};
export default IntakeForm;
